import { Button, FlexContainer } from '@axxes/design-system';
import { faCogs, faHistory } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { useAppDispatch, useAppSelector } from '../../common/store/hooks';
import { sortCV } from '../../common/utils/sorters';
import AboutMeView from '../../users/components/About/AboutMeView';
import { Project, UserCV } from '../../users/model';
import { CertificatesView } from '../components/PdfEditor/CertificatesView/CertificatesView';
import { EducationsView } from '../components/PdfEditor/EducationsView/EducationsView';
import { LanguagesView } from '../components/PdfEditor/LanguagesView/LanguagesView';
import { ProjectsView } from '../components/PdfEditor/ProjectsView/ProjectsView';
import { SkillsView } from '../components/PdfEditor/SkillsView/SkillsView';
import { TrainingsView } from '../components/PdfEditor/TrainingsView/TrainingsView';
import { WeblinksView } from '../components/PdfEditor/WeblinksView/WeblinksView';
import { DownloadPdfService } from '../services/downloadPdfService';
import { getCvCopy, getInvisibles, getMinimizeds } from '../store/selectors';
import { resetGeneratorPages, updateCvCopy } from '../store/slice';
import './editPdfContainer.scss';

export const EditPdfContainerV2 = (): JSX.Element => {

  const dispatch = useAppDispatch();
  const editingCv = useAppSelector(getCvCopy);
  const invisibles = useAppSelector(getInvisibles);
  const minimizeds = useAppSelector(getMinimizeds);

  const [loading, setLoading] = React.useState<boolean>(false);
  const [errored, setErrored] = React.useState<boolean>(false);

  const resetOrder = () => {
    dispatch(resetGeneratorPages());
    dispatch(updateCvCopy(sortCV(editingCv, true)));
  };

  const elements = {
    'about': <AboutMeView userInfo={editingCv?.userInfo} user={editingCv?.user} isLoading={false} />,
    'skills': <SkillsView editingCV={editingCv} />,
    'projects': <ProjectsView editingCV={editingCv} />,
    'educations': <EducationsView editingCV={editingCv} />,
    'certificates': <CertificatesView editingCV={editingCv} />,
    'trainings': <TrainingsView editingCV={editingCv} />,
    'languages': <LanguagesView editingCV={editingCv} />,
    'weblinks': <WeblinksView editingCV={editingCv} />,
  };

  const getCVForDownload = (cv: UserCV): UserCV => {
    return {
      ...cv,
      skills: cv.skills?.filter((s) => !invisibles.includes(s.id || '')),
      projects: cv.projects?.filter(p => !invisibles.includes(p.id || ''))?.map(p => {
        return {
          ...p,
          displayOption: minimizeds.includes(p.id || '') ? 'MINIMIZED' : 'STANDARD'
        };
      }) || [],
      educations: cv.educations?.filter(e => !invisibles.includes(e.id || '')).map(e => {
        return {
          ...e,
          description: minimizeds.includes(e.id || '') ? [] : e.description
        };
      }),
      certificates: cv.certificates?.filter(c => !invisibles.includes(c.id || '')).map(c => {
        return {
          ...c,
          description: minimizeds.includes(c.id || '') ? [] : c.description
        };
      }),
      trainings: cv.trainings?.filter(t => !invisibles.includes(t.id || '')).map(t => {
        return {
          ...t,
          description: minimizeds.includes(t.id || '') ? [] : t.description
        };
      }),
    }
  };

  const downloadPdf = () => {
    setLoading(true);
    const cv: UserCV = getCVForDownload(editingCv || {});
    DownloadPdfService.downloadPdf(cv).catch(() => setErrored(true)).finally(() => setLoading(false));
  };

  const messageContainer = () => {
    if (loading) {
      return (
        <FlexContainer
          customClasses="axxes-generate__container"
          grow={1}
          padding="0px"
          direction="column"
        >
          <span className="axxes-generate__text -loading">Generating your PDF</span>
        </FlexContainer>
      );
    }
    if (errored) {
      return (
        <FlexContainer
          customClasses="axxes-generate__container"
          grow={1}
          padding="0px"
          direction="column"
        >
          <span className="axxes-generate__text -loading">An error occurred while generating your PDF. Please try again later.</span>
        </FlexContainer>
      );
    }
    return null;
  };


  return messageContainer() || (
    <FlexContainer
      padding='0'
      direction='column'
      grow={1}
    >
      <FlexContainer padding='0' customClasses='axxes-edit-pdf-action'>
        <Button
          variant='ghost'
          onClick={resetOrder}
        >
          <FlexContainer padding='0'>
            <FontAwesomeIcon icon={faHistory} />
            <span>Reset Order</span>
          </FlexContainer>
        </Button>

        <Button
          accent={true}
          onClick={downloadPdf}
        >
          <FlexContainer padding='0'>
            <FontAwesomeIcon icon={faCogs} />
            <span>Download</span>
          </FlexContainer>
        </Button>
      </FlexContainer>
      <FlexContainer
        direction='column'
        customClasses='axxes-edit-pdf'
      >
        <div className='axxes-edit-pdf__pages-container'>
          {
            Object.entries(elements).map(([sectionType, element]) => {
              return (
                <div key={sectionType} className={`axxes-edit-pdf-pdf-block`}>
                  <div className={'axxes-edit-pdf-selected-card'}>
                    {element}
                  </div>
                </div>
              );
            })
          }
        </div>
      </FlexContainer>
    </FlexContainer>
  );
};

export default EditPdfContainerV2;

